import {Info} from 'lucide-react';

import {cn} from '../../lib/utils';
import {Dot} from '../Dot';
import {ProgressBar} from '../ProgressBar';
import {Tooltip} from '../Tooltip';
import {Typography} from '../Typography';

import type {FC, ReactNode} from 'react';

/**
 * `'dynamic'` — bar color is determined by progress thresholds (success/info/warning).
 * `'static'` — bar is always warning amber regardless of value.
 * `'indigo'` / `'lavender'` / `'lime'` — fixed brand fills, not tied to a status.
 */
export type MetricProgressColor = 'dynamic' | 'static' | 'indigo' | 'lavender' | 'lime';

export interface MetricColumnConfig {
  label: string;
  value: string;
  /** Secondary label shown opposite the main label (e.g. "7%") */
  sublabel?: string;
  /** Extra Tailwind class(es) on the value element, e.g. `"text-text-error"` */
  valueClassName?: string;
  /** Extra Tailwind class(es) on the sublabel element, e.g. `"text-text-success"` */
  sublabelClassName?: string;
  /** Tooltip text shown on hover of an info icon next to the label */
  tooltip?: string;
  progress?: {
    /** 0–100 */
    value: number;
    color: MetricProgressColor;
    tooltipText?: string;
    /** Row under the bar — `start` is the emphasized figure, `end` the muted remainder. */
    caption?: {start: ReactNode; end?: ReactNode};
  };
}

function resolveBarColor(color: MetricProgressColor, value: number): string {
  if (color === 'lime') return 'bg-progress-fill';
  if (color === 'indigo') return 'bg-brand-primary';
  if (color === 'lavender') return 'bg-progress-fill-secondary';
  if (color === 'static') return 'bg-indication-warning';
  if (value >= 70) return 'bg-indication-success';
  if (value >= 50) return 'bg-indication-info';
  return 'bg-indication-warning';
}

const MetricColumn: FC<MetricColumnConfig> = ({
  label,
  value,
  sublabel,
  valueClassName,
  sublabelClassName,
  tooltip,
  progress
}) => {
  const barColor = progress ? resolveBarColor(progress.color, progress.value) : '';

  return (
    <div className="flex flex-1 flex-col items-start justify-between border-e border-border-subtle px-6 py-8 last:border-e-0">
      {/* Label + value */}
      <div className="flex w-full flex-col items-start gap-1.5">
        <div className="flex w-full items-center justify-between">
          <div className="inline-flex items-center gap-1">
            {progress && <Dot size="md" ring className="me-1" colorClassName={barColor} />}
            <Typography size="xs" color="secondary" className="text-start">
              {label}
            </Typography>
            {tooltip && (
              <Tooltip content={tooltip} side="top">
                <Info className="size-3 shrink-0 text-fg-tertiary opacity-60" aria-hidden="true" />
              </Tooltip>
            )}
          </div>
          {sublabel && (
            <Typography
              size="xs"
              color="secondary"
              className={cn('tabular-nums', sublabelClassName)}
            >
              {sublabel}
            </Typography>
          )}
        </div>
        <Typography
          size="display-xs"
          weight="semibold"
          className={cn('tabular-nums', valueClassName)}
        >
          {value}
        </Typography>
      </div>

      {/* Progress bar pinned to bottom */}
      <div className="group relative mt-4 w-full">
        {progress !== undefined && (
          <>
            <ProgressBar value={progress.value} fillClassName={barColor} aria-label={label} />
            {progress.caption && (
              <Typography
                as="div"
                size="xs"
                color="tertiary"
                className="mt-2 flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-0.5 whitespace-normal tabular-nums"
              >
                <span>{progress.caption.start}</span>
                {progress.caption.end && <span>{progress.caption.end}</span>}
              </Typography>
            )}
            {progress.tooltipText && (
              <div className="pointer-events-none absolute bottom-full start-0 end-0 mb-2 flex justify-center opacity-0 transition-opacity group-hover:opacity-100">
                <div className="rounded-lg border border-border-subtle bg-background-surface px-3 py-2 shadow-lg">
                  <Typography size="xs" weight="semibold" className="whitespace-nowrap">
                    {progress.tooltipText}
                  </Typography>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export interface MetricsCardProps {
  columns: MetricColumnConfig[];
}

/**
 * Generic multi-column metrics card with optional progress bars and tooltips.
 * Pass a `columns` config array — each entry renders as one column.
 */
export const MetricsCard: FC<MetricsCardProps> = ({columns}) => (
  <div
    data-slot="metrics-card"
    className="flex w-full overflow-hidden rounded-xl border border-border-subtle shadow-xs"
  >
    {columns.map(col => (
      <MetricColumn key={col.label} {...col} />
    ))}
  </div>
);
